import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, ArrowRight, Lock, Loader2, X } from 'lucide-react';
import { db, auth } from '../firebase';
import { 
  signInWithPopup, 
  GoogleAuthProvider, 
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ServiceModal } from '../components/ServiceModal';
import sparkLogo from '../assets/spark-logo.png';

const FALLBACK_BLOCKED_DOMAINS = [
  'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.in', 'yahoo.co.uk',
  'outlook.com', 'hotmail.com', 'live.com', 'msn.com', 'icloud.com', 'me.com',
  'aol.com', 'protonmail.com', 'proton.me', 'zoho.com', 'yandex.com',
  'mail.com', 'gmx.com'
];

export const AuthPage = () => {
  const navigate = useNavigate();
  
  // States
  const [emailMode, setEmailMode] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [blockedDomains, setBlockedDomains] = useState(FALLBACK_BLOCKED_DOMAINS);
  const [isTerminated, setIsTerminated] = useState(false);
  
  // Modal State
  const [modalConfig, setModalConfig] = useState({
    visible: false, title: '', message: '', buttons: []
  });

  const hasAttemptedLogin = useRef(false);

  // Fetch blocklist on mount
  useEffect(() => {
    const fetchBlockedDomains = async () => {
      try {
        const docRef = doc(db, 'app_config', 'auth_restrictions');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists() && Array.isArray(docSnap.data().blocked_domains)) {
          setBlockedDomains(docSnap.data().blocked_domains);
        }
      } catch (error) {
        // console.warn("Using fallback domains");
      }
    };
    fetchBlockedDomains();
  }, []);

  const location = useLocation();

  // Redirect if already logged in and setup complete
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user && !isSigningIn && !isSignInWithEmailLink(auth, window.location.href)) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists() && userDoc.data().isSetupComplete) {
            const redirectParam = new URLSearchParams(location.search).get('redirect') || new URLSearchParams(window.location.search).get('redirect');
            const targetRedirect = redirectParam ? decodeURIComponent(redirectParam) : '/';
            navigate(targetRedirect, { replace: true });
          }
        } catch (error) {
          // console.error("Error checking auth state on AuthPage:", error);
        }
      }
    });
    return () => unsubscribe();
  }, [navigate, location.search, isSigningIn]);

  // Handle Magic Link return (if user clicks the link from email)
  useEffect(() => {
    const handleEmailLinkSignIn = async () => {
      if (isSignInWithEmailLink(auth, window.location.href)) {
        if (hasAttemptedLogin.current) return;
        hasAttemptedLogin.current = true;
        
        let email = window.localStorage.getItem('emailForSignIn');
        if (!email) {
          email = window.prompt('Please provide your email for confirmation');
        }
        if (email) {
          try {
            setIsSigningIn(true);
            const result = await signInWithEmailLink(auth, email, window.location.href);
            window.localStorage.removeItem('emailForSignIn');
            
            // Check User Doc
            const user = result.user;
            const userDocSnap = await getDoc(doc(db, 'users', user.uid));
            
            const redirect = new URLSearchParams(window.location.search).get('redirect');
            const encodedRedirect = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';

            if (userDocSnap.exists()) {
                if (userDocSnap.data().isSetupComplete) {
                   navigate(redirect || '/');
                } else {
                   navigate(`/college-selection${encodedRedirect}`);
                }
            } else {
                // Create Doc
                await setDoc(doc(db, 'users', user.uid), {
                    name: user.displayName || '',
                    email: user.email || '',
                    photoURL: user.photoURL || '',
                    uid: user.uid,
                    isSetupComplete: false,
                });
                navigate(`/college-selection${encodedRedirect}`);
            }
          } catch (error) {
            if (error.code === 'auth/user-disabled') {
              setIsTerminated(true);
            } else {
              showModal('Sign in Error', error.message);
            }
          } finally {
            setIsSigningIn(false);
          }
        }
      }
    };
    handleEmailLinkSignIn();
  }, [navigate]);

  const showModal = (title, message, buttons = []) => {
    setModalConfig({ visible: true, title, message, buttons });
  };

  const closeModal = () => setModalConfig({ ...modalConfig, visible: false });

  // --- GOOGLE SIGN IN ---
  const handleGoogleSignIn = async () => {
    try {
      setIsSigningIn(true);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      if (!user.email) throw new Error('Google Sign-In failed: Missing email');

      const domain = user.email.split('@')[1].toLowerCase();
      
      if (blockedDomains.includes(domain)) {
        let isWhitelisted = false;
        try {
          const docRef = doc(db, 'whitelisted_emails', user.email);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) isWhitelisted = true;
        } catch (error) {}

        if (!isWhitelisted) {
          setIsSigningIn(false);
          await auth.signOut();
          showModal(
            'Student ID Required',
            'Spark is for verified students only. Please use your official .edu email.\n\nNot on Gmail? Use the "CONTINUE WITH COLLEGE MAIL" button below.',
            [{ text: 'Understood', onClick: closeModal }]
          );
          return;
        }
      }
      
      // Check User Doc
      const userDocSnap = await getDoc(doc(db, 'users', user.uid));
      
      if (userDocSnap.exists()) {
          if (userDocSnap.data().isSetupComplete) {
              navigate('/');
          } else {
              navigate('/college-selection');
          }
      } else {
          // Create Doc
          await setDoc(doc(db, 'users', user.uid), {
              name: user.displayName || '',
              email: user.email || '',
              photoURL: user.photoURL || '',
              uid: user.uid,
              isSetupComplete: false,
          });
          navigate('/college-selection');
      }

    } catch (error) {
      if (error.code === 'auth/user-disabled') {
        setIsTerminated(true);
        return;
      }
      if (error.code !== 'auth/popup-closed-by-user') {
        showModal('Connection Error', 'We couldn’t connect to Google. Please try again.');
      }
    } finally {
      setIsSigningIn(false);
    }
  };

  // --- EMAIL SIGN IN ---
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    const email = emailInput.trim().toLowerCase();
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return showModal('Error', 'Please enter a valid email address.');
    }

    const domain = email.split('@')[1];
    if (blockedDomains.includes(domain)) {
      let isWhitelisted = false;
      try {
        const docRef = doc(db, 'whitelisted_emails', email);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) isWhitelisted = true;
      } catch (error) {}

      if (!isWhitelisted) {
        return showModal('Access Denied', 'Spark is exclusive to verified university students. Please use your official College/University ID.');
      }
    }

    try {
      setIsSigningIn(true);
      
      const actionCodeSettings = {
        url: window.location.origin + '/login', // Web callback URL
        handleCodeInApp: true,
      };

      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', email);
      
      showModal(
        'Check your Inbox', 
        `We’ve sent a secure access link to ${email}.\n\nClick that link to get in! (Check Spam also)`, 
        [{ text: 'Got it', onClick: closeModal }]
      );
      
    } catch (error) {
      if (error.code === 'auth/user-disabled') {
        setIsTerminated(true);
      } else {
        showModal('Error', `Failed: ${error.message}`);
      }
    } finally {
      setIsSigningIn(false);
    }
  };

  return (
    <div className="relative min-h-[100dvh] bg-black flex flex-col items-center justify-center overflow-x-hidden font-poppins text-white pb-24 sm:pb-0">
      
      {/* Background Styling */}
      <div className="absolute inset-0 z-0">
         <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(11,217,229,0.15),transparent_70%)]" />
         <div className="absolute inset-0 bg-[linear-gradient(#0BD9E5_1px,transparent_1px),linear-gradient(90deg,#0BD9E5_1px,transparent_1px)] bg-[size:50px_50px] opacity-[0.03]" />
      </div>

      <div className="relative z-10 w-full max-w-md px-6 flex flex-col items-center justify-between min-h-[80vh]">
        
        {/* Branding & Logo */}
        <div className="flex flex-col items-center mt-14 sm:mt-16">
          <div className="flex items-center gap-2 px-4 py-1.5 rounded-full border border-gray-800 bg-black/50 backdrop-blur-sm mb-6">
            <div className="w-2 h-2 rounded-full bg-[#0BD9E5] animate-pulse" />
            <span className="text-gray-400 text-xs font-bold tracking-widest">EST. 2025</span>
          </div>

          {/* Spark Logo */}
          <img 
            src={sparkLogo} 
            alt="Spark" 
            className="h-24 sm:h-32 w-auto object-contain drop-shadow-[0_0_45px_rgba(11,217,229,0.45)] mb-3" 
          />

          {/* Description right below Spark Logo */}
          <p className="text-gray-400 max-w-xs text-center text-sm">The Dual-Identity campus social network</p>
        </div>

        {/* Auth Actions */}
        <div className="w-full flex flex-col items-center gap-4 mb-8">
          
          <AnimatePresence mode="wait">
            {!emailMode ? (
              <motion.div 
                key="default"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="w-full flex flex-col items-center gap-3"
              >
                {/* VIBE CHECK text (commented out) */}
                {/* 
                <div className="flex items-center justify-center gap-2 mb-1">
                  <h1 className="text-3xl sm:text-4xl font-black tracking-tighter leading-none">VIBE</h1>
                  <h1 className="text-3xl sm:text-4xl font-black tracking-tighter leading-none text-transparent" style={{ WebkitTextStroke: '1px white' }}>CHECK.</h1>
                </div>
                */}

                {/* Laser Badge Placeholder */}
                <div className="flex items-center gap-2 bg-black px-4 py-1.5 rounded-full border border-[#0BD9E5]/30 shadow-[0_0_15px_rgba(11,217,229,0.2)] mb-4">
                  <Lock size={12} className="text-gray-400" />
                  <span className="text-gray-400 text-[10px] font-bold tracking-wider">STUDENT ID REQUIRED</span>
                </div>

                <button 
                  onClick={handleGoogleSignIn}
                  disabled={isSigningIn}
                  className="w-full bg-white hover:bg-gray-100 text-black h-14 rounded-xl flex items-center justify-center gap-3 font-bold tracking-wide transition-colors disabled:opacity-70"
                >
                  {isSigningIn ? <Loader2 className="animate-spin w-5 h-5" /> : (
                    <>
                      <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
                      CONTINUE WITH GMAIL
                    </>
                  )}
                </button>

                <button 
                  onClick={() => setEmailMode(true)}
                  className="w-full bg-[#1a1a1a] hover:bg-[#222] border border-gray-800 text-white h-14 rounded-xl flex items-center justify-center gap-3 font-bold tracking-wide transition-colors"
                >
                  <Mail size={20} />
                  CONTINUE WITH MAIL
                </button>
              </motion.div>
            ) : (
              <motion.form 
                key="email"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onSubmit={handleEmailLogin}
                className="w-full flex flex-col items-center"
              >
                <label className="text-white font-bold mb-4">Enter your student email</label>
                <div className="relative w-full">
                  <input 
                    type="email"
                    required
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="student@university.edu"
                    className="w-full bg-[#111] border border-gray-800 rounded-xl h-14 pl-4 pr-14 text-white focus:outline-none focus:border-[#0BD9E5] transition-colors"
                  />
                  <button 
                    type="submit"
                    disabled={isSigningIn || !emailInput}
                    className="absolute right-2 top-2 bottom-2 w-10 bg-white rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    {isSigningIn ? <Loader2 className="animate-spin text-black w-5 h-5" /> : <ArrowRight className="text-black w-5 h-5" />}
                  </button>
                </div>
                <button 
                  type="button"
                  onClick={() => setEmailMode(false)}
                  className="mt-6 text-gray-500 text-sm underline hover:text-gray-300 transition-colors"
                >
                  Go back
                </button>
              </motion.form>
            )}
          </AnimatePresence>

          {!emailMode && (
            <p className="text-gray-500 text-[11px] mt-6 text-center leading-relaxed">
              By joining, you agree to the <a href="https://www.sparkpl.com/terms-and-conditions.html" className="text-white underline font-bold">Terms</a> & <a href="https://www.sparkpl.com/privacy-policy.html" className="text-white underline font-bold">Privacy Policy</a>.
            </p>
          )}

        </div>
      </div>

      {/* Bottom Right Info Links */}
      <div className="relative sm:fixed mt-8 sm:mt-0 bottom-auto sm:bottom-6 left-0 right-0 sm:left-auto sm:right-6 flex flex-row sm:flex-col flex-wrap justify-center sm:justify-end sm:items-end gap-x-4 gap-y-2 px-4 sm:px-0 z-20 pb-8 sm:pb-0">
        <button 
          onClick={() => navigate('/info')}
          className="text-gray-400 hover:text-white text-xs sm:text-sm font-medium transition-colors"
        >
          Know more
        </button>
        <button 
          onClick={() => navigate('/info')}
          className="text-gray-400 hover:text-white text-xs sm:text-sm font-medium transition-colors"
        >
          Can't find your college?
        </button>
        <button 
          onClick={() => navigate('/info/community')}
          className="text-gray-400 hover:text-white text-xs sm:text-sm font-medium transition-colors"
        >
          Community
        </button>
        <button 
          onClick={() => navigate('/info/contact')}
          className="text-gray-400 hover:text-white text-xs sm:text-sm font-medium transition-colors"
        >
          Contact
        </button>
      </div>

      {/* Modal Overlay */}
      <AnimatePresence>
        {modalConfig.visible && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          >
            <motion.div 
              initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
              className="bg-[#111] border border-gray-800 p-6 rounded-2xl w-full max-w-sm relative shadow-2xl"
            >
              <h3 className="text-xl font-bold text-white mb-2 text-center">{modalConfig.title}</h3>
              <p className="text-gray-400 text-center mb-6 text-sm leading-relaxed max-w-xs mx-auto whitespace-pre-line">{modalConfig.message}</p>
              
              <div className="flex gap-3 justify-center">
                {modalConfig.buttons?.length > 0 ? (
                  modalConfig.buttons.map((btn, i) => (
                    <button 
                      key={i} onClick={btn.onClick}
                      className="bg-white text-black font-bold py-3 px-8 rounded-xl hover:bg-gray-200 transition-colors"
                    >
                      {btn.text}
                    </button>
                  ))
                ) : (
                  <button 
                    onClick={closeModal}
                    className="bg-white text-black font-bold py-3 px-8 rounded-xl hover:bg-gray-200 transition-colors"
                  >
                    OK
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <ServiceModal 
          visible={isTerminated}
          type="TERMINATED"
          onClose={() => setIsTerminated(false)}
        />
    </div>
  );
};

export default AuthPage;
